import { Button, Chip } from '@heroui/react'
import { Icon } from '@iconify/react'
import { useState } from 'react'

import { BrandMark } from './BrandMark'
import { applyLang, dicts, pickLang, saveLang, type Lang } from './i18n'

const CONSOLE_URL = 'https://app.clotho.moira-e.com'
const DOCS_URL = 'https://docs.clotho.moira-e.com'
const WALLET_REPO = 'https://gitlab.moira-e.com/clotho/clotho-wallet'
const APP_VERSION = 'Android 8+'
const MANIFEST_URL = 'https://oss.moira-e.com/clotho-releases/clotho-wallet/manifest.json'

export function App() {
  const [lang, setLang] = useState<Lang>(() => {
    const l = pickLang()
    applyLang(l)
    return l
  })
  const t = dicts[lang]

  const toggleLang = () => {
    const next: Lang = lang === 'zh' ? 'en' : 'zh'
    saveLang(next)
    setLang(next)
  }

  return (
    <div className="relative overflow-x-clip">
      {/* ===== Nav ===== */}
      <header className="border-border/60 bg-background/70 sticky top-0 z-40 border-b backdrop-blur-md">
        <nav className="mx-auto flex h-14 w-full max-w-6xl items-center gap-6 px-6">
          <a href="/" className="flex items-center gap-2 no-underline">
            <BrandMark className="text-accent size-7" title="clotho" />
            <span className="text-foreground text-sm font-semibold tracking-tight">clotho</span>
          </a>
          <div className="text-muted hidden items-center gap-5 text-sm sm:flex">
            <a href="#features" className="hover:text-foreground no-underline transition-colors">
              {t.nav.features}
            </a>
            <a href={DOCS_URL} className="hover:text-foreground no-underline transition-colors">
              {t.nav.docs}
            </a>
            <a href={CONSOLE_URL} className="hover:text-foreground no-underline transition-colors">
              {t.nav.console}
            </a>
          </div>
          <span className="flex-1" />
          <Button variant="ghost" size="sm" onPress={toggleLang}>
            {lang === 'zh' ? 'EN' : '中文'}
          </Button>
          <Button
            size="sm"
            onPress={() => window.open(`${CONSOLE_URL}/`, '_self')}
          >
            {t.nav.start}
          </Button>
        </nav>
      </header>

      {/* ===== Hero ===== */}
      <section className="hero-grid relative">
        <span className="glow bg-accent left-[8%] top-[-60px] h-72 w-72" />
        <span className="glow left-[55%] top-[120px] h-80 w-80 bg-[oklch(0.7_0.15_200)]" />
        <div className="relative mx-auto grid w-full max-w-6xl gap-12 px-6 pb-20 pt-20 lg:grid-cols-2 lg:items-center lg:pt-28">
          <div className="flex flex-col items-start gap-6">
            <Chip size="sm" color="accent" variant="soft">
              {t.hero.badge}
            </Chip>
            <h1 className="text-5xl font-bold leading-[1.08] tracking-tight sm:text-6xl">
              {t.hero.title1}
              <br />
              <span className="gradient-text">{t.hero.title2}</span>
            </h1>
            <p className="text-muted max-w-xl text-lg leading-relaxed">{t.hero.sub}</p>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" onPress={() => window.open(`${CONSOLE_URL}/`, '_self')}>
                {t.hero.ctaStart}
                <Icon icon="gravity-ui:arrow-right" className="ml-1 size-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onPress={() => window.open(DOCS_URL, '_blank', 'noopener')}
              >
                {t.hero.ctaDocs}
              </Button>
            </div>
          </div>

          {/* Terminal + webhook stack */}
          <div className="relative flex flex-col gap-4">
            <TerminalCard title={t.hero.terminalTitle}>
              <span className="text-[oklch(0.75_0.12_150)]">$</span> curl
              api.clotho.moira-e.com/api/v1/orders \{'\n'}
              {'  '}-H <Str>"Authorization: Bearer ck_test_…"</Str> \{'\n'}
              {'  '}-H <Str>"Idempotency-Key: order-0001"</Str> \{'\n'}
              {'  '}-d <Str>{`'{"external_id":"order-0001",`}</Str>
              {'\n'}
              {'      '}
              <Str>{`"chain":"tron","amount_micro":5000000}'`}</Str>
              {'\n\n'}
              <Dim>{'// → 201'}</Dim>
              {'\n'}
              {`{`}
              {'\n  '}
              <Key>"deposit_address"</Key>: <Str>"TX7y…fQ2k"</Str>,{'\n  '}
              <Key>"pay_url"</Key>: <Str>"https://pay.clotho…/0197…"</Str>
              {'\n'}
              {`}`}
            </TerminalCard>
            <div className="float-slow self-end lg:mr-[-12px] lg:w-[85%]">
              <TerminalCard title={t.hero.webhookTitle} accent>
                <Dim>POST /your/webhook · X-Clotho-Signature ✓</Dim>
                {'\n'}
                {`{`}
                {'\n  '}
                <Key>"type"</Key>: <Str>"order.paid"</Str>,{'\n  '}
                <Key>"data"</Key>: {`{ `}
                <Key>"paid_micro"</Key>: <Num>5000000</Num>
                {` }`}
                {'\n'}
                {`}`}
              </TerminalCard>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Stats strip ===== */}
      <section className="border-border/60 border-y">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-2 gap-px px-6 py-10 sm:grid-cols-4">
          {t.stats.map((s) => (
            <div key={s.label} className="flex flex-col items-center gap-1 text-center">
              <span className="text-3xl font-bold tabular-nums tracking-tight">{s.value}</span>
              <span className="text-muted text-xs">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Features ===== */}
      <section id="features" className="mx-auto w-full max-w-6xl px-6 py-24">
        <div className="mb-12 flex flex-col items-center gap-3 text-center">
          <h2 className="text-3xl font-bold tracking-tight">{t.features.title}</h2>
          <p className="text-muted max-w-xl">{t.features.sub}</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {t.features.items.map((f) => (
            <div
              key={f.title}
              className="bg-surface group rounded-2xl p-6 shadow-surface transition-transform hover:-translate-y-0.5"
            >
              <div className="bg-accent-soft text-accent-soft-foreground mb-4 flex size-10 items-center justify-center rounded-xl">
                <Icon icon={f.icon} className="size-5" />
              </div>
              <h3 className="mb-1.5 font-semibold">{f.title}</h3>
              <p className="text-muted text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Gateway compatibility ===== */}
      <section className="border-border/60 border-t">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-6 px-6 py-20 text-center">
          <h2 className="text-3xl font-bold tracking-tight">{t.compat.title}</h2>
          <p className="text-muted max-w-2xl">{t.compat.sub}</p>
          <div className="flex flex-wrap justify-center gap-3">
            {t.compat.names.map((name) => (
              <span
                key={name}
                className="bg-surface text-foreground rounded-full px-4 py-2 text-sm font-medium shadow-surface"
              >
                {name}
              </span>
            ))}
          </div>
          <a
            href={`${DOCS_URL}/gateways.html`}
            className="text-accent text-sm font-medium no-underline hover:underline"
          >
            {t.compat.link}
          </a>
        </div>
      </section>

      {/* ===== Wallet app ===== */}
      <section className="border-border/60 border-t">
        <div className="mx-auto grid w-full max-w-6xl gap-10 px-6 py-20 lg:grid-cols-2 lg:items-center">
          <div className="flex flex-col items-start gap-5">
            <Chip size="sm" color="accent" variant="soft">
              <Icon icon="gravity-ui:smartphone" className="mr-1 size-3.5" />
              {APP_VERSION}
            </Chip>
            <h2 className="text-3xl font-bold tracking-tight">{t.app.title}</h2>
            <p className="text-muted max-w-xl leading-relaxed">{t.app.sub}</p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={async () => {
                  try {
                    const res = await fetch(`${MANIFEST_URL}?_t=${Date.now()}`, {
                      cache: 'no-store',
                    })
                    const m = (await res.json()) as { apk_url: string }
                    if (m.apk_url) window.location.href = m.apk_url
                  } catch {
                    // Fall back to a known versioned URL so the button never dead-ends.
                    window.location.href = 'https://gitlab.moira-e.com/clotho/clotho-wallet/-/tags'
                  }
                }}
                className="bg-accent text-accent-foreground inline-flex items-center justify-center gap-1 rounded-md px-5 py-3 text-base font-semibold no-underline shadow-surface transition-opacity hover:opacity-90"
              >
                <Icon icon="gravity-ui:arrow-down-to-line" className="size-4" />
                {t.app.download}
              </button>
              <Button size="lg" variant="outline" onPress={() => window.open(WALLET_REPO, '_blank', 'noopener')}>
                {t.app.source}
              </Button>
            </div>
            <p className="text-muted flex items-center gap-1.5 text-xs">
              <Icon icon="gravity-ui:lock" className="size-3.5" />
              {t.app.note}
            </p>
          </div>
          {/* Phone mock */}
          <div className="relative mx-auto w-full max-w-[280px]">
            <div className="bg-surface ring-border/60 relative overflow-hidden rounded-[2.5rem] p-3 shadow-surface ring-1">
              <div className="bg-background overflow-hidden rounded-[2rem] p-5">
                <div className="mb-5 flex items-center gap-2">
                  <span className="bg-accent text-accent-foreground flex size-6 items-center justify-center rounded-md text-xs font-bold">C</span>
                  <span className="text-sm font-semibold">clotho</span>
                </div>
                <div className="bg-surface-secondary mb-4 rounded-2xl p-4">
                  <div className="text-muted text-[10px]">Confirmed across all chains</div>
                  <div className="text-2xl font-bold tabular-nums">$12,480.00</div>
                </div>
                {[
                  { c: 'TRON', v: '8,200.00', color: 'oklch(0.65 0.2 25)' },
                  { c: 'BSC', v: '2,980.00', color: 'oklch(0.8 0.15 85)' },
                  { c: 'Base', v: '1,300.00', color: 'oklch(0.55 0.2 255)' },
                ].map((r) => (
                  <div key={r.c} className="bg-surface mb-2 flex items-center justify-between rounded-xl px-3 py-2.5">
                    <span className="flex items-center gap-2 text-xs font-medium">
                      <span className="size-2 rounded-full" style={{ backgroundColor: r.color }} />
                      {r.c}
                    </span>
                    <span className="text-sm font-semibold tabular-nums">${r.v}</span>
                  </div>
                ))}
                <div className="bg-accent text-accent-foreground mt-3 rounded-xl py-2.5 text-center text-xs font-semibold">
                  Sweep to main wallet
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section className="border-border/60 border-t">
        <div className="mx-auto w-full max-w-6xl px-6 py-24">
          <h2 className="mb-12 text-center text-3xl font-bold tracking-tight">{t.how.title}</h2>
          <div className="grid gap-8 sm:grid-cols-3">
            {t.how.steps.map((step, i) => (
              <div key={step.title} className="flex flex-col items-center gap-3 text-center">
                <span className="border-border text-muted flex size-10 items-center justify-center rounded-full border text-sm font-semibold">
                  {i + 1}
                </span>
                <h3 className="font-semibold">{step.title}</h3>
                <p className="text-muted max-w-60 text-sm">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="relative overflow-hidden">
        <span className="glow bg-accent left-1/2 top-10 h-64 w-[40rem] -translate-x-1/2" />
        <div className="relative mx-auto flex w-full max-w-6xl flex-col items-center gap-5 px-6 py-24 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t.cta.title}</h2>
          <p className="text-muted max-w-lg">{t.cta.sub}</p>
          <Button size="lg" onPress={() => window.open(`${CONSOLE_URL}/`, '_self')}>
            {t.cta.button}
            <Icon icon="gravity-ui:arrow-right" className="ml-1 size-4" />
          </Button>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="border-border/60 border-t">
        <div className="text-muted mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-6 gap-y-2 px-6 py-8 text-sm">
          <span className="flex items-center gap-2">
            <BrandMark className="text-accent size-5" />
            clotho
          </span>
          <span className="flex-1" />
          <a href={DOCS_URL} className="hover:text-foreground no-underline">
            {t.footer.docs}
          </a>
          <a href={CONSOLE_URL} className="hover:text-foreground no-underline">
            {t.footer.console}
          </a>
          <a href={`${DOCS_URL}/api.html`} className="hover:text-foreground no-underline">
            {t.footer.api}
          </a>
        </div>
      </footer>
    </div>
  )
}

/* ---- terminal building blocks ---------------------------------------- */

function TerminalCard({
  title,
  accent,
  children,
}: {
  title: string
  accent?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      className={[
        'bg-surface overflow-hidden rounded-2xl shadow-surface',
        accent ? 'ring-accent/30 ring-1' : '',
      ].join(' ')}
    >
      <div className="border-border/60 flex items-center gap-2 border-b px-4 py-2.5">
        <span className="flex gap-1.5">
          <span className="size-2.5 rounded-full bg-[oklch(0.65_0.2_25)]" />
          <span className="size-2.5 rounded-full bg-[oklch(0.8_0.15_85)]" />
          <span className="size-2.5 rounded-full bg-[oklch(0.7_0.17_150)]" />
        </span>
        <span className="text-muted ml-2 text-xs">{title}</span>
      </div>
      <pre className="overflow-x-auto p-4 text-xs leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  )
}

const Str = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[oklch(0.78_0.12_150)]">{children}</span>
)
const Key = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[oklch(0.75_0.12_240)]">{children}</span>
)
const Num = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[oklch(0.78_0.14_70)]">{children}</span>
)
const Dim = ({ children }: { children: React.ReactNode }) => (
  <span className="text-muted">{children}</span>
)
